import { Injectable } from '@angular/core';
import { LayoutConfig } from '../models/layout.model';
import { LayoutStateService } from '../states/layout-state.service';
import { computed, Signal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  constructor(private layoutState: LayoutStateService) {
    console.log("[LayoutService] Service initialized");
  }

  /**
   * Get a signal for a layout configuration
   * @param layoutId ID of the layout to retrieve
   * @returns Signal with the layout configuration
   */
  layoutConfig(layoutId: string): Signal<LayoutConfig | undefined> {
    console.log(`[LayoutService] Requesting config for layout ${layoutId}`);

    return computed(() => {
      const layout = this.layoutState.getLayout(layoutId);

      if (layout) {
        console.log(`[LayoutService] Found existing config for ${layoutId}`);
        return layout;
      }

      console.warn(`[LayoutService] No config found for ${layoutId}, generating placeholder`);
      return this.generatePlaceholderLayout(layoutId);
    });
  }

  /**
   * Get all layout configurations
   */
  get allLayouts() {
    return this.layoutState.allLayouts;
  }

  /**
   * Get layout configurations for multiple IDs
   * @param layoutIds Array of layout IDs to retrieve
   * @returns Array of layout configurations
   */
  getLayoutConfigsSync(layoutIds: string[]): LayoutConfig[] {
    console.log(`[LayoutService] Getting multiple layouts for IDs: ${layoutIds.join(', ')}`);
    
    return layoutIds
      .map(id => {
        console.log(`[LayoutService] Getting layout for ID: ${id}`, this.layoutState.allLayouts());
        // First check the state
        const layout = this.layoutState.getLayout(id);
        
        if (layout) {
          return layout;
        }
        
        console.warn(`[LayoutService] No layout found for ID: ${id}, will generate placeholder`);
        return this.generatePlaceholderLayout(id);
      })
      .filter(layout => !!layout); // Filter out any undefined layouts
  }

  /**
   * Generate a placeholder layout when actual layout is not found
   * @param layoutId ID of the layout to generate
   * @returns Generated placeholder layout
   */
  private generatePlaceholderLayout(layoutId: string): LayoutConfig {
    console.log(`[LayoutService] Generating placeholder layout for ID: ${layoutId}`);
    
    // Create a minimal layout with the requested ID
    return {
      layoutId: layoutId,
      pageId: '', // Will be filled by page component
      cards: [],
    };
  }

  /**
   * Add or update a layout in the state
   * @param layout Layout configuration to store
   */
  setLayoutConfig(layout: LayoutConfig): void {
    console.log(`[LayoutService] Setting layout configuration for ID: ${layout.layoutId}`);
    this.layoutState.addLayout(layout.layoutId, layout);
  }

  /**
   * Update specific properties of a layout configuration
   * @param id Layout ID to update
   * @param updates Partial layout configuration with properties to update
   */
  updateLayoutConfig(id: string, updates: Partial<LayoutConfig>): void {
    console.log(`[LayoutService] Updating layout configuration for ID: ${id}`);
    this.layoutState.updateLayout(id, updates);
  }

  /**
   * Delete a layout configuration
   * @param id Layout ID to delete
   */
  deleteLayout(id: string): void {
    console.log(`[LayoutService] Deleting layout configuration for ID: ${id}`);
    this.layoutState.deleteLayout(id);
  }

  /**
   * Clear all layout configurations
   * Call this when switching pages to avoid layout reuse
   */
  clearLayouts(): void {
    console.log(`[LayoutService] Clearing all layouts`);
    this.layoutState.clearAllLayouts();
  }
} 